import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';

interface Offer {
  templateId: string;
  label: string;
  description: string;
  fromName: string;
  toName: string;
  reward: number;
  durationMs: number;
}

interface ActiveRun {
  templateId: string;
  label: string;
  state: 'assigned' | 'in_progress';
  stepLabel: string;
  fromName: string;
  toName: string;
  reward: number;
  deadline: number;
}

interface Snapshot {
  serverNow: number;
  cooldownUntil: number;
  dailyPaid: number;
  dailyCap: number;
  active: ActiveRun | null;
  offers: Offer[];
}

// Same click-grace guard as CraftMenu/TargetMenu: browser focus flaps while
// the overlay is toggled on, so ignore clicks (and Escape) for a short
// window after opening.
const CLICK_GRACE_MS = 250;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (dev preview) - log instead.
    // eslint-disable-next-line no-console
    console.log('publicWork sendMessage', payload);
  }
};

function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatMinutes(ms: number): string {
  return `${Math.round(ms / 60_000)} min`;
}

const PublicWork: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [snap, setSnap] = useState<Snapshot | null>(null);
  // serverNow - local Date.now() at receipt, so countdowns follow the server clock.
  const [offset, setOffset] = useState(0);
  const [, setTick] = useState(0);
  const openedAtRef = useRef(0);

  useEffect(() => {
    const apply = (s: Snapshot | undefined | null) => {
      if (!s) return;
      setSnap(s);
      setOffset((typeof s.serverNow === 'number' ? s.serverNow : Date.now()) - Date.now());
    };
    const showHandler = (e: Event) => {
      apply(((e as CustomEvent).detail as { state?: Snapshot } | null)?.state);
      openedAtRef.current = Date.now();
      setVisible(true);
    };
    const stateHandler = (e: Event) => apply(((e as CustomEvent).detail as { state?: Snapshot } | null)?.state);
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showPublicWork', showHandler);
    window.addEventListener('skymp5-client:publicWorkState', stateHandler);
    window.addEventListener('skymp5-client:hidePublicWork', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showPublicWork', showHandler);
      window.removeEventListener('skymp5-client:publicWorkState', stateHandler);
      window.removeEventListener('skymp5-client:hidePublicWork', hideHandler);
    };
  }, []);

  // 1 s clock for the countdowns, only while the panel is shown.
  useEffect(() => {
    if (!visible) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [visible]);

  const graceOver = () => Date.now() - openedAtRef.current >= CLICK_GRACE_MS;

  const close = () => {
    if (!graceOver()) return;
    setVisible(false);
    send({ publicWorkAction: 'close' });
  };

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;
      setVisible(false);
      send({ publicWorkAction: 'close' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  if (!visible || !snap) return null;

  const now = Date.now() + offset;
  const cooldownMs = Math.max(0, snap.cooldownUntil - now);
  const capReached = snap.dailyPaid >= snap.dailyCap;
  const blocked = cooldownMs > 0 || capReached;

  const accept = (templateId: string) => {
    if (!graceOver() || blocked) return;
    send({ publicWorkAction: 'accept', templateId });
  };
  const abandon = () => {
    if (!graceOver()) return;
    send({ publicWorkAction: 'abandon' });
  };

  const active = snap.active;
  const remaining = active ? active.deadline - now : 0;

  return (
    <div className="public-work-overlay">
      <div className="public-work-panel">
        <div className="public-work-header">
          <div className="public-work-title">Travail public</div>
        </div>

        <div className="public-work-body">
          {active ? (
            <div className="public-work-status">
              <div className="public-work-status-name">{active.label}</div>
              <dl className="public-work-facts">
                <div>
                  <dt>Étape</dt>
                  <dd>{active.stepLabel}</dd>
                </div>
                <div>
                  <dt>Itinéraire</dt>
                  <dd>{active.fromName} vers {active.toName}</dd>
                </div>
                <div>
                  <dt>Destination</dt>
                  <dd>{active.state === 'assigned' ? `${active.fromName} (chargement), puis ${active.toName}` : active.toName}</dd>
                </div>
                <div>
                  <dt>Temps restant</dt>
                  <dd className={remaining < 60_000 ? 'public-work-warn' : ''}>{formatClock(remaining)}</dd>
                </div>
                <div>
                  <dt>Paie</dt>
                  <dd>{active.reward} Septims</dd>
                </div>
              </dl>
              <div className="public-work-actions">
                <button type="button" className="public-work-btn public-work-btn--danger" onClick={abandon}>
                  Abandonner
                </button>
              </div>
            </div>
          ) : snap.offers.length === 0 ? (
            <div className="public-work-empty">Aucun travail disponible pour le moment.</div>
          ) : (
            <ul className="public-work-list">
              {snap.offers.map((o) => (
                <li className="public-work-offer" key={o.templateId}>
                  <div className="public-work-offer-main">
                    <div className="public-work-offer-name">{o.label}</div>
                    <div className="public-work-offer-route">{o.fromName} vers {o.toName}</div>
                    <div className="public-work-offer-meta">
                      {o.reward} Septims, {formatMinutes(o.durationMs)} maximum
                    </div>
                  </div>
                  <button
                    type="button"
                    className="public-work-btn"
                    disabled={blocked}
                    onClick={() => accept(o.templateId)}
                  >
                    Accepter
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="public-work-footer">
          <div className="public-work-limits">
            {cooldownMs > 0 && <span>Repos : encore {formatClock(cooldownMs)}</span>}
            <span className={capReached ? 'public-work-warn' : ''}>
              Gains du jour : {snap.dailyPaid} / {snap.dailyCap} Septims
            </span>
          </div>
          <button type="button" className="public-work-btn public-work-btn--quiet" onClick={close}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicWork;
